import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { 
  DollarSign, 
  Users, 
  TrendingUp, 
  Package,
  ArrowUpIcon,
  RefreshCw,
  AlertTriangle,
  Shield,
  Lock,
  ChevronDown
} from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import orderService from '../../../services/orderService'
import ApiService from '../../../services/api'
import AppURLs from '../../../config/urls'
import SalesTrendChart from '../../../components/dashboard/SalesTrendChart'
import AuthService from '../../../services/authService'
import BranchService from '../../../services/branchService'
import { getDateRange, parseDBDate } from '../../../utils/timezone'

export const Dashboard = () => {
  const navigate = useNavigate();
  const user = useMemo(() => AuthService.getCurrentUser(), []);
  const hasLoadedData = useRef(false);
  const [loading, setLoading] = useState(false)
  const [recentSalesLoading, setRecentSalesLoading] = useState(false)
  const [lowStocksLoading, setLowStocksLoading] = useState(false)
  const [orderTypes, setOrderTypes] = useState({
    dine_in: { count: 0, percentage: 0 },
    take_away: { count: 0, percentage: 0 },
    delivery: { count: 0, percentage: 0 }
  })
  const [recentSales, setRecentSales] = useState([])
  const [lowStocks, setLowStocks] = useState([])
  const [branches, setBranches] = useState([])
  const [selectedBranchId, setSelectedBranchId] = useState('all')
  const [orderTypeDateRange, setOrderTypeDateRange] = useState('month') // 'today', 'week', 'month'
  const [dashboardData, setDashboardData] = useState({
    totalSale: 0,
    activeCustomers: 0,
    monthlyGrowth: 0,
    monthlyOrders: 0
  })

  // Check if user has dashboard access
  const hasAccessToDashboard = useCallback(() => {
    if (!user) return false;
    const allowedRoles = ['super_admin', 'admin', 'manager', 'owner'];
    return allowedRoles.includes(user.role?.toLowerCase());
  }, [user])

  // Get user's branch ID for filtering
  const getUserBranchId = useCallback(() => {
    if (!user) return null;
    
    // For super_admin/admin/owner, return null to see all data
    if (user.role?.toLowerCase() === 'super_admin' || user.role?.toLowerCase() === 'admin' || user.role?.toLowerCase() === 'owner') {
      return null;
    }
    
    // For manager, return their assigned branch
    if (user.role?.toLowerCase() === 'manager') {
      return user.branchId || null;  // Assuming branchId is stored in user object
    }
    
    return null;
  }, [user]);

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M'
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K'
    }
    return num.toString()
  }

  const loadOrderTypes = useCallback(async () => {
    try {
      setLoading(true)
      
      const branchId = getUserBranchId();
      
      // Calculate date range based on filter using Sri Lanka timezone
      const { fromDate, toDate } = getDateRange(orderTypeDateRange)
      
      console.log('📅 Order Types Filter:', {
        range: orderTypeDateRange,
        fromDate: fromDate.toISOString().split('T')[0],
        toDate: toDate.toISOString().split('T')[0]
      });
      
      const baseParams = branchId ? { branch: branchId } : {};
      const queryParams = {
        ...baseParams,
        fromDate: fromDate.toISOString().split('T')[0],
        toDate: toDate.toISOString().split('T')[0],
        orderStatus: 'completed'
      }

      console.log('🔍 API Query Params:', queryParams)
      const response = await ApiService.get(AppURLs.ORDERS.GET_ALL, queryParams)
      console.log('📦 API Response:', response)
      
      if (response.success && response.data?.orders) {
        // Use all orders from API (backend already filtered by date)
        const filteredOrders = response.data.orders.filter(order => {
          return order.orderStatus === 'completed'
        });
        
        console.log('📊 Total orders from API:', response.data.orders.length);
        console.log('📊 Orders after status filter:', filteredOrders.length);
        console.log('📊 Sample order data:', filteredOrders.slice(0, 2));
        
        // Calculate dashboard statistics
        const totalSale = filteredOrders.reduce((sum, order) => {
          return sum + (parseFloat(order.orderPayment?.totalAmount || order.totalAmount || 0))
        }, 0)
        
        const uniqueCustomers = new Set(filteredOrders.map(order => 
          order.customer?._id || order.customerId
        )).size
        
        const monthlyOrders = filteredOrders.length
        
        // Update dashboard stats
        setDashboardData({
          totalSale: totalSale,
          activeCustomers: uniqueCustomers,
          monthlyGrowth: 0, // TODO: Calculate growth if previous period data available
          monthlyOrders: monthlyOrders
        })
        
        const typesSummary = filteredOrders.reduce((acc, order) => {
          const type = order.orderType || 'Unknown'
          if (!acc[type]) {
            acc[type] = { count: 0, total: 0 }
          }
          acc[type].count += 1
          acc[type].total += order.totalAmount || 0
          return acc
        }, {})

        // Calculate total orders for percentage calculation
        const totalOrders = Object.values(typesSummary).reduce((sum, data) => sum + data.count, 0)
        
        // Format data to match UI expectations
        const formattedTypes = {
          dine_in: {
            count: typesSummary['dine-in']?.count || typesSummary['dine_in']?.count || 0,
            percentage: totalOrders > 0 ? Math.round(((typesSummary['dine-in']?.count || typesSummary['dine_in']?.count || 0) / totalOrders) * 100) : 0
          },
          take_away: {
            count: typesSummary['take-away']?.count || typesSummary['take_away']?.count || typesSummary['takeaway']?.count || 0,
            percentage: totalOrders > 0 ? Math.round(((typesSummary['take-away']?.count || typesSummary['take_away']?.count || typesSummary['takeaway']?.count || 0) / totalOrders) * 100) : 0
          },
          delivery: {
            count: typesSummary['delivery']?.count || 0,
            percentage: totalOrders > 0 ? Math.round((typesSummary['delivery']?.count || 0) / totalOrders * 100) : 0
          }
        }

        setOrderTypes(formattedTypes)
      } else {
        setOrderTypes({
          dine_in: { count: 0, percentage: 0 },
          take_away: { count: 0, percentage: 0 },
          delivery: { count: 0, percentage: 0 }
        })
        setDashboardData({
          totalSale: 0,
          activeCustomers: 0,
          monthlyGrowth: 0,
          monthlyOrders: 0
        })
      }
    } catch (error) {
      console.error('Error loading order types:', error)
      setOrderTypes([])
      setDashboardData({
        totalSale: 0,
        activeCustomers: 0,
        monthlyGrowth: 0,
        monthlyOrders: 0
      })
    } finally {
      setLoading(false)
    }
  }, [getUserBranchId, orderTypeDateRange])

  // Load recent sales data
  const loadRecentSales = useCallback(async () => {
    try {
      setRecentSalesLoading(true);
      
      const branchId = getUserBranchId();
      const baseParams = branchId ? { branch: branchId } : {};
      
      // Try different parameter combinations
      let response = await orderService.getAllOrders({
        ...baseParams,
        orderStatus: 'complete'
      });
      
      // If no data with 'complete', try 'completed'
      if (!response.success || !response.data?.orders?.length) {
        response = await orderService.getAllOrders({
          ...baseParams,
          orderStatus: 'completed'
        });
      }
      
      // If still no data, try without status filter to see all orders
      if (!response.success || !response.data?.orders?.length) {
        response = await orderService.getAllOrders(baseParams);
      }
      
      if (response.success && response.data?.orders) {
        // Filter for completed orders if we got all orders
        let ordersToProcess = response.data.orders;
        if (!response.data.orders.some(order => 
          order.orderStatus === 'complete' || order.orderStatus === 'completed'
        )) {
          ordersToProcess = response.data.orders.filter(order => 
            order.orderStatus === 'complete' || 
            order.orderStatus === 'completed' ||
            order.status === 'complete' ||
            order.status === 'completed'
          );
        }
        
        // Format and limit to latest 5 sales
        const formattedSales = ordersToProcess
          .sort((a, b) => parseDBDate(b.orderCreatedAt || b.createdAt) - parseDBDate(a.orderCreatedAt || a.createdAt))
          .slice(0, 5)
          .map(order => {
            const orderDate = parseDBDate(order.orderCreatedAt || order.createdAt)
            
            // Format time (already in Sri Lanka time, just format it)
            const timeFormatted = orderDate.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            })
            
            return {
              id: order.orderNumber || order.order_number || order.id,
              phone: order.customerPhone || order.customerMobile || order.customer_phone || 'Walk-in',
              amount: parseFloat(order.orderPayment?.totalAmount || order.totalAmount || order.total || 0),
              time: timeFormatted,
              branch: order.branchName || order.branch?.name || order.branchName || 'Unknown Branch'
            }
          });
        
        setRecentSales(formattedSales);
      } else {
        setRecentSales([]);
      }
    } catch (error) {
      console.error('Error loading recent sales:', error);
      setRecentSales([]);
    } finally {
      setRecentSalesLoading(false);
    }
  }, [getUserBranchId]);

  // Load branches data
  const loadBranches = useCallback(async () => {
    try {
      const response = await BranchService.getAllBranches();
      if (response.success && response.data?.branches) {
        setBranches(response.data.branches);
      }
    } catch (error) {
      console.error('Error loading branches:', error);
    }
  }, []);

  // Load low stock items data
  const loadLowStocks = useCallback(async () => {
    try {
      setLowStocksLoading(true);
      
      // Determine which branch to filter by
      let branchId = null;
      
      // If user has a specific branch (manager), use that branch
      const userBranchId = getUserBranchId();
      if (userBranchId) {
        branchId = userBranchId;
      } else if (selectedBranchId !== 'all') {
        // Otherwise, use the selected branch from dropdown
        branchId = selectedBranchId;
      }
      
      const params = branchId ? { branch: branchId } : {};
      
      // Get stock alerts data
      const response = await ApiService.get(AppURLs.STOCK_ALERTS.GET_ALL, params);
      
      if (response.success && response.data?.items) {
        // Filter for low stock and out of stock items
        const lowStockItems = response.data.items
          .filter(item => 
            item.stockStatus === 'low_stock' || 
            item.stockStatus === 'out_of_stock' ||
            item.currentStock <= item.threshold
          )
          .slice(0, 5) // Limit to 5 items for dashboard
          .map(item => ({
            id: item._id,
            name: item.name,
            type: item.itemType === 'raw_material' ? 'Raw Material' : 'Product',
            quantity: item.currentStock,
            min: item.threshold,
            status: item.stockStatus,
            unit: item.unit?.abbreviation || 'units',
            branchName: item.branchName || item.branch?.name || 'Unknown Branch'
          }));

        setLowStocks(lowStockItems);
      } else {
        setLowStocks([]);
      }
    } catch (error) {
      console.error('Error loading low stocks:', error);
      setLowStocks([]);
    } finally {
      setLowStocksLoading(false);
    }
  }, [getUserBranchId, selectedBranchId]);

  useEffect(() => {
    if (!hasLoadedData.current && hasAccessToDashboard()) {
      hasLoadedData.current = true;
      loadBranches();
      loadOrderTypes();
      loadRecentSales();
      loadLowStocks();
    }
  }, [hasAccessToDashboard, loadBranches, loadOrderTypes, loadRecentSales, loadLowStocks])

  // Reload low stocks when branch filter changes
  useEffect(() => {
    if (hasLoadedData.current) {
      loadLowStocks();
    }
  }, [selectedBranchId, loadLowStocks])

  // Reload order types when date range changes
  useEffect(() => {
    if (hasLoadedData.current) {
      loadOrderTypes();
    }
  }, [orderTypeDateRange, loadOrderTypes])

  // Show access denied message for users without dashboard access
  if (!hasAccessToDashboard()) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="text-6xl text-gray-300">🚫</div>
        <h2 className="text-2xl font-semibold text-gray-700">Access Denied</h2>
        <p className="text-gray-500 text-center max-w-md">
          You don't have permission to view the dashboard. Only super admins, admins, managers, and owners can access this section.
        </p>
        <Button 
          variant="outline" 
          onClick={() => window.history.back()}
          className="mt-4"
        >
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <Button variant="outline" size="sm">
          View Reports
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Total Sale
            </CardTitle>
            <DollarSign className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              Rs.{formatNumber(dashboardData.totalSale)}
            </div>
            
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Active Customer
            </CardTitle>
            <Users className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(dashboardData.activeCustomers)}
            </div>
            
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Monthly Growth
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {dashboardData.monthlyGrowth}%
            </div>
            
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">
              Monthly Orders
            </CardTitle>
            <Package className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(dashboardData.monthlyOrders)}
            </div>
           
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SalesTrendChart />

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Orders by Type</CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={loadOrderTypes}
              disabled={loading}
            >
              <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            </Button>
          </CardHeader>
          <CardContent>
            {/* Date Range Filter */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <button
                onClick={() => setOrderTypeDateRange('today')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  orderTypeDateRange === 'today'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Today
              </button>
              <button
                onClick={() => setOrderTypeDateRange('week')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  orderTypeDateRange === 'week'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                This Week
              </button>
              <button
                onClick={() => setOrderTypeDateRange('month')}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  orderTypeDateRange === 'month'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                This Month
              </button>
            </div>
            {loading ? (
              <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <RefreshCw className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-2" />
                  <p className="text-gray-500">Loading order types...</p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="h-32 flex items-end justify-center space-x-4">
                  <div className="flex flex-col items-center">
                    <div 
                      className="w-16 bg-blue-500 rounded-t-lg transition-all duration-500 ease-in-out"
                      style={{ height: Math.max(orderTypes.take_away?.percentage || 0, 5) + 'px' }}
                    ></div>
                    <span className="text-xs text-gray-600 mt-2">Take Away</span>
                    <span className="text-sm font-medium">{orderTypes.take_away?.percentage || 0}%</span>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div 
                      className="w-16 bg-green-500 rounded-t-lg transition-all duration-500 ease-in-out"
                      style={{ height: Math.max(orderTypes.dine_in?.percentage || 0, 5) + 'px' }}
                    ></div>
                    <span className="text-xs text-gray-600 mt-2">Dine-In</span>
                    <span className="text-sm font-medium">{orderTypes.dine_in?.percentage || 0}%</span>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div 
                      className="w-16 bg-orange-500 rounded-t-lg transition-all duration-500 ease-in-out"
                      style={{ height: Math.max(orderTypes.delivery?.percentage || 0, 5) + 'px' }}
                    ></div>
                    <span className="text-xs text-gray-600 mt-2">Delivery</span>
                    <span className="text-sm font-medium">{orderTypes.delivery?.percentage || 0}%</span>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-2 bg-blue-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-4 h-4 bg-blue-500 rounded"></div>
                      <span className="text-sm font-medium">Take Away</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold">{orderTypes.take_away?.count || 0} orders</div>
                      <div className="text-xs text-gray-600">{orderTypes.take_away?.percentage || 0}%</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-4 h-4 bg-green-500 rounded"></div>
                      <span className="text-sm font-medium">Dine-In</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold">{orderTypes.dine_in?.count || 0} orders</div>
                      <div className="text-xs text-gray-600">{orderTypes.dine_in?.percentage || 0}%</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-2 bg-orange-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-4 h-4 bg-orange-500 rounded"></div>
                      <span className="text-sm font-medium">Delivery</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold">{orderTypes.delivery?.count || 0} orders</div>
                      <div className="text-xs text-gray-600">{orderTypes.delivery?.percentage || 0}%</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Sales</CardTitle>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/admin/sales')}
            >
              View All
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentSalesLoading ? (
                <div className="flex items-center justify-center py-4">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">Loading recent sales...</span>
                </div>
              ) : recentSales.length > 0 ? (
                recentSales.map((sale) => (
                  <div key={sale.id} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Order #{sale.id}</p>
                      <p className="text-sm text-gray-500">{sale.phone}</p>
                      {sale.branch && (
                        <p className="text-xs text-blue-600">{sale.branch}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-medium">Rs.{sale.amount.toFixed(2)}</p>
                      <p className="text-sm text-gray-500">{sale.time}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-gray-500">
                  No recent sales found
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Low Stocks
            </CardTitle>
            <div className="flex items-center gap-2">
              {/* Branch filter dropdown - only show for super_admin/admin/owner */}
              {(user?.role?.toLowerCase() === 'super_admin' || user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'owner') && (
                <div className="relative">
                  <select
                    value={selectedBranchId}
                    onChange={(e) => setSelectedBranchId(e.target.value)}
                    className="appearance-none px-3 py-1.5 pr-8 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
                  >
                    <option value="all">All Branches</option>
                    {branches.map((branch) => (
                      <option key={branch._id} value={branch._id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              )}
              <Button 
                variant="ghost" 
                size="sm"
                onClick={loadLowStocks}
                disabled={lowStocksLoading}
              >
                <RefreshCw className={lowStocksLoading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {lowStocksLoading ? (
                <div className="flex items-center justify-center py-4">
                  <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">Loading stock alerts...</span>
                </div>
              ) : lowStocks.length > 0 ? (
                lowStocks.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 rounded-lg border border-orange-200 bg-orange-50">
                    <div>
                      <p className="font-medium text-gray-900">{item.name}</p>
                      <p className="text-sm text-gray-600">{item.type}</p>
                      {selectedBranchId === 'all' && (
                        <p className="text-xs text-blue-600 mt-1">{item.branchName}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className={`font-medium ${
                        item.status === 'out_of_stock' ? 'text-red-600' : 'text-orange-600'
                      }`}>
                        {item.quantity} {item.unit} {item.status === 'out_of_stock' ? '(Out)' : 'left'}
                      </p>
                      <p className="text-sm text-gray-500">Min: {item.min}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Package className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 font-medium">No low stock items</p>
                  <p className="text-sm text-gray-400">All items are well stocked</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
